import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../components/Layout.jsx';
// import MainMenu from '../components/MainMenu.jsx';
import video from '../assets/lookbook/lookbook-sped-up.mov';
import { client } from '../lib/client.js';
import { lookbookQuery } from '../lib/queries.js';

function buildSanityUrl(baseUrl, { w, h, fit = 'max', q = 75 } = {}) {
  if (!baseUrl) return '';
  try {
    const u = new URL(baseUrl);
    if (w) u.searchParams.set('w', String(w));
    if (h) u.searchParams.set('h', String(h));
    if (fit) u.searchParams.set('fit', fit);
    u.searchParams.set('auto', 'format');
    u.searchParams.set('q', String(q));
    return u.toString();
  } catch {
    const parts = [];
    if (w) parts.push(`w=${w}`);
    if (h) parts.push(`h=${h}`);
    if (fit) parts.push(`fit=${fit}`);
    parts.push('auto=format', `q=${q}`);
    return baseUrl + (baseUrl.includes('?') ? '&' : '?') + parts.join('&');
  }
}

function Lookbook() {
  const [images, setImages] = useState([]); // [{full, thumb}] after transform
  const [activeMediaIndex, setActiveMediaIndex] = useState(-1); // -1 = video
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const videoRef = useRef(null);

  // Fetch & transform URLs once
  useEffect(() => {
    let mounted = true;
    client.fetch(lookbookQuery).then((data) => {
      if (!mounted) return;
      const items = (data || []).map((item) => {
        const full = buildSanityUrl(item.url, { w: 1600, fit: 'max', q: 80 });
        const thumb = buildSanityUrl(item.url, { w: 160, h: 160, fit: 'crop', q: 70 });
        return { full, thumb };
      });
      setImages(items);
    });
    return () => { mounted = false; };
  }, []);

  // Preload neighbor images in order for snappier nav
  useEffect(() => {
    if (!images.length) return;
    const idx = activeMediaIndex;
    const preload = (i) => {
      if (i < 0 || i >= images.length) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = images[i].full;
    };
    if (idx === -1) {
      preload(0);
    } else {
      preload((idx + 1) % images.length);
      preload((idx - 1 + images.length) % images.length);
    }
  }, [activeMediaIndex, images]);

  const currentMedia =
    activeMediaIndex === -1 ? video : images[activeMediaIndex]?.full;

  const handleVideoClick = () => {
    if (videoRef.current) {
      isVideoPlaying ? videoRef.current.pause() : videoRef.current.play();
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const handleThumbnailClick = (index) => {
    setActiveMediaIndex(index);
    if (index === -1 && videoRef.current) {
      videoRef.current.play();
      setIsVideoPlaying(true);
    } else {
      setIsVideoPlaying(false);
    }
  };

  const handlePrev = () => {
    setActiveMediaIndex((prev) =>
      prev === -1 ? images.length - 1 : prev === 0 ? -1 : prev - 1
    );
    setIsVideoPlaying(false);
  };

  const handleNext = () => {
    setActiveMediaIndex((prev) =>
      prev === images.length - 1 ? -1 : prev + 1
    );
    setIsVideoPlaying(false);
  };

  // Priority hinting: give the currently shown visual higher priority
  const priorityProps =
    activeMediaIndex === -1
      ? {}
      : { fetchPriority: 'high', loading: 'eager' };

  const canonicalUrl = 'https://this-belongs-to.com/lookbook';
  const metaTitle = 'Lookbook | This Belongs To';
  const metaDescription =
    'Moving lookbook film and stills from This Belongs To, showing layered underwear, loungewear and clothing.';

  return (
    <Layout>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>

      {/* <MainMenu /> */}
      <div className="flex flex-col items-center md:justify-center min-h-screen space-y-6 md:mt-10">
        <div className="relative w-full max-w-4xl h-[300px] md:h-[550px] mb-8">
          {activeMediaIndex === -1 ? (
            <video
              ref={videoRef}
              src={video}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              onClick={handleVideoClick}
              className="w-full h-full object-cover cursor-pointer"
            />
          ) : (
            <img
              src={currentMedia}
              alt="Lookbook image"
              className="w-full h-full object-cover cursor-pointer"
              decoding="async"
              {...priorityProps}
              onClick={() => setIsVideoPlaying(false)}
            />
          )}

          <button
            onClick={handlePrev}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 px-2 py-1"
          >
            &#9664;
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1"
          >
            &#9654;
          </button>
        </div>

        {/* Thumbnails */}
        <div className="grid grid-cols-4 md:grid-cols-12 gap-2 gap-y-4 mt-4 max-w-4xl">
          {/* Video thumb */}
          <div
            className={`w-16 h-16 cursor-pointer ${
              activeMediaIndex === -1 ? 'border-2 border-blue-500' : ''
            }`}
            onClick={() => handleThumbnailClick(-1)}
            title="Video"
          >
            <video
              src={video}
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Image thumbs */}
          {images.map((image, index) => (
            <img
              key={index}
              src={image.thumb}
              alt={`Thumbnail ${index + 1}`}
              className={`w-16 h-16 object-cover cursor-pointer ${
                activeMediaIndex === index ? 'border-2 border-blue-500' : ''
              }`}
              onClick={() => handleThumbnailClick(index)}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
          ))}
        </div>
      </div>
    </Layout>
  );
}

export default Lookbook;
